import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { FaEye } from "react-icons/fa";

import "./Support.css";
import CreateOfferModal from "../../components/Dashboard/CreateOfferModal";

const API = "https://api.mahal.qa/api/v1/admin/support";

export default function AllTickets() {

  const [tickets, setTickets] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [assigned, setAssigned] = useState("");
  const [queueId, setQueueId] = useState("");

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [previewFile, setPreviewFile] = useState(null);
  const [attachmentUrls, setAttachmentUrls] = useState({});

  const [selectedIds, setSelectedIds] = useState([]);

  const [admins, setAdmins] = useState([]);
  const [replyMessage, setReplyMessage] = useState("");
  const [internalNote, setInternalNote] = useState("");

  const containerRef = useRef();

  const ADMIN_TOKEN =
    localStorage.getItem("admin_token");

  const ADMIN_ROLE =
    localStorage.getItem("admin_role");


  const headers = {
    Authorization: `Bearer ${ADMIN_TOKEN}`
  };

  /* =======================================================
     SEARCH DEBOUNCE
  ======================================================= */

  useEffect(() => {

    const t = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(t);

  }, [search]);

  /* =======================================================
     AUTO REFRESH
  ======================================================= */

  useEffect(() => {

    const interval = setInterval(() => {
      fetchTickets(true);
    }, 30000);

    return () => clearInterval(interval);

  }, [
    debouncedSearch,
    status,
    priority,
    assigned,
    queueId
  ]);

  /* =======================================================
     FETCH ADMINS
  ======================================================= */

  const fetchAssignableAdmins = async (
    ticketId
  ) => {

    try {

      const res = await axios.get(
        `${API}/ticket/${ticketId}/assignable-admins`,
        { headers }
      );

      setAdmins(
        res.data.admins || []
      );

    } catch (err) {

      console.error(err);

      setAdmins([]);
    }
  };

  /* =======================================================
     FETCH TICKETS
  ======================================================= */

  const fetchTickets = async (
    reset = false
  ) => {

    if (loading) return;

    try {

      setLoading(true);

      const res = await axios.get(
        `${API}/tickets`,
        {
          headers,
          params: {
            cursor:
              reset ? null : cursor,

            search: debouncedSearch,

            status,

            priority,

            queue_id:
              queueId || null,

            assigned_admin_id:
              assigned || null,

            limit: 30
          }
        }
      );

      const newTickets =
        res.data.data || [];

      setTickets(prev =>
        reset
          ? newTickets
          : [...prev, ...newTickets]
      );

      setCursor(
        res.data.next_cursor
      );

      setHasMore(
        !!res.data.next_cursor
      );

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Failed to fetch tickets"
      );

    } finally {

      setLoading(false);
    }
  };

  useEffect(() => {

    fetchTickets(true);

  }, [
    debouncedSearch,
    status,
    priority,
    assigned,
    queueId
  ]);

  /* =======================================================
     SCROLL
  ======================================================= */

  const handleScroll = () => {

    const el = containerRef.current;

    if (!el || loading || !hasMore) {
      return;
    }

    if (
      el.scrollTop + el.clientHeight >=
      el.scrollHeight - 50
    ) {
      fetchTickets();
    }
  };

  /* =======================================================
     ATTACHMENT FETCH
  ======================================================= */

  const fetchAttachment = async (id) => {

    try {

      const res = await axios.get(
        `${API}/attachment/${id}`,
        {
          headers,
          responseType: "blob"
        }
      );

      return URL.createObjectURL(
        res.data
      );

    } catch {

      return null;
    }
  };



  const openTicket = async (id) => {

    try {

      const res = await axios.get(
        `${API}/ticket/${id}`,
        { headers }
      );

      setSelectedTicket(
        res.data
      );


      await fetchAssignableAdmins(id);


      setModalOpen(true);



      await axios.post(
        `${API}/ticket/${id}/mark-viewed`,
        {},
        { headers }
      );

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Failed to load ticket"
      );
    }
  };



  useEffect(() => {

    document.body.classList.toggle(

      modalOpen
    );

  }, [modalOpen]);

  /* =======================================================
     LOAD ATTACHMENTS
  ======================================================= */

  useEffect(() => {

    const loadAttachments =
      async () => {

        if (!selectedTicket) {
          return;
        }

        const map = {};

        for (const a of selectedTicket.attachments) {

          if (
            /\.(jpg|jpeg|png|gif|webp)$/i.test(
              a.file_name
            )
          ) {

            const url =
              await fetchAttachment(
                a.attachment_id
              );

            if (url) {
              map[a.attachment_id] = url;
            }
          }
        }

        setAttachmentUrls(map);
      };

    if (modalOpen) {
      loadAttachments();
    }

  }, [modalOpen, selectedTicket]);

  /* =======================================================
     BULK UPDATE
  ======================================================= */

  const bulkUpdate = async (
    status
  ) => {

    if (!selectedIds.length) {
      return;
    }

    try {

      await axios.post(
        `${API}/tickets/bulk-update`,
        {
          ticket_ids: selectedIds,
          status
        },
        { headers }
      );

      setSelectedIds([]);

      await fetchTickets(true);

      alert(
        "Bulk update success"
      );

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Bulk update failed"
      );
    }
  };

  /* =======================================================
     STATUS
  ======================================================= */

  const changeStatus = async (
    id,
    status
  ) => {

    try {

      await axios.post(
        `${API}/ticket/${id}/status`,
        { status },
        { headers }
      );

      await openTicket(id);

      await fetchTickets(true);

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Status update failed"
      );
    }
  };

  /* =======================================================
     PRIORITY
  ======================================================= */

  const changePriority = async (
    id,
    priority
  ) => {

    try {

      await axios.post(
        `${API}/ticket/${id}/priority`,
        { priority },
        { headers }
      );

      await openTicket(id);

      await fetchTickets(true);

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Priority update failed"
      );
    }
  };

  /* =======================================================
     ESCALATE
  ======================================================= */

  const escalate = async (id) => {

    try {

      const res = await axios.post(
        `${API}/ticket/${id}/escalate`,
        {},
        { headers }
      );

      alert(res.data.message);

      await openTicket(id);

      await fetchTickets(true);

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Escalation failed"
      );
    }
  };

  /* =======================================================
     DE-ESCALATE
  ======================================================= */

  const deEscalate = async (id) => {

    try {

      const res = await axios.post(
        `${API}/ticket/${id}/de-escalate`,
        {},
        { headers }
      );

      alert(res.data.message);

      await openTicket(id);

      await fetchTickets(true);

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "De-escalation failed"
      );
    }
  };

  /* =======================================================
     REOPEN
  ======================================================= */

  const reopenTicket = async (
    id
  ) => {

    try {

      const res = await axios.post(
        `${API}/ticket/${id}/reopen`,
        {},
        { headers }
      );

      alert(res.data.message);

      await openTicket(id);

      await fetchTickets(true);

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Reopen failed"
      );
    }
  };

  /* =======================================================
     UNASSIGN
  ======================================================= */

  const unassignTicket = async (
    id
  ) => {

    try {

      const res = await axios.post(
        `${API}/ticket/${id}/unassign`,
        {},
        { headers }
      );

      alert(res.data.message);

      await openTicket(id);

      await fetchTickets(true);

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Unassign failed"
      );
    }
  };

  /* =======================================================
     ASSIGN
  ======================================================= */

  const assignTicket = async (
    ticketId,
    adminId
  ) => {

    try {

      await axios.post(
        `${API}/ticket/${ticketId}/assign`,
        {
          admin_id: adminId
        },
        { headers }
      );

      await openTicket(ticketId);

      await fetchTickets(true);

      alert(
        "Assignment updated"
      );

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Assignment failed"
      );
    }
  };

  /* =======================================================
     INTERNAL NOTE
  ======================================================= */

  const addInternalNote = async (
    id
  ) => {

    if (!internalNote.trim()) {
      return;
    }

    try {

      await axios.post(
        `${API}/ticket/${id}/note`,
        {
          note: internalNote
        },
        { headers }
      );

      setInternalNote("");

      await openTicket(id);

      alert("Note added");

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Failed to add note"
      );
    }
  };

  /* =======================================================
     ADMIN REPLY
  ======================================================= */

  const sendReply = async (
    id
  ) => {

    if (!replyMessage.trim()) {
      return;
    }

    if (
      selectedTicket.ticket.status === "closed"
    ) {
      return alert(
        "Cannot reply to closed ticket"
      );
    }

    if (
      selectedTicket.ticket.status === "resolved"
    ) {
      return alert(
        "Reopen ticket before replying"
      );
    }

    try {

      await axios.post(
        `${API}/ticket/${id}/reply`,
        {
          message: replyMessage
        },
        { headers }
      );

      setReplyMessage("");

      await openTicket(id);

      alert("Reply sent");

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.error ||
        "Reply failed"
      );
    }
  };

  /* =======================================================
     ACTIONS
  ======================================================= */

  const getAllowedActions = (
    ticket
  ) => {

    const actions = [];

    const status =
      ticket.status;

    if (status === "open") {
      actions.push(
        "in_progress",
        "closed"
      );
    }

    if (status === "in_progress") {
      actions.push(
        "waiting",
        "resolved",
        "closed"
      );
    }

    if (status === "waiting") {
      actions.push(
        "in_progress",
        "closed"
      );
    }

    if (status === "resolved") {
      actions.push(
        "closed"
      );
    }

    return actions;
  };


  if (!ADMIN_TOKEN) {
    return (
      <div >
        Session expired. Login again.
      </div>
    );
  }

  if (ADMIN_ROLE !== "SUPER_ADMIN") {
    return (
      <div>
        Access Denied
      </div>
    );
  }

  return (

    <div className="zt_page">

      {/* ===================================================
          HEADER
      =================================================== */}

      <div className="support-panel1">

        <div className="support-panel-header">

          <div className="support-panel-title">

            <span>
              TICKET MANAGEMENT
            </span>

            <h3>
              All Tickets
            </h3>

          </div>

        </div>

        <div className="support-filters">

          <input
            className="support-search"
            placeholder="Search ticket..."
            value={search}
            onChange={(e) =>
              setSearch(
                e.target.value
              )
            }
          />

          <select
            className="support-select"
            value={status}
            onChange={(e) =>
              setStatus(
                e.target.value
              )
            }
          >

            <option value="">
              Status
            </option>

            <option value="open">
              Open
            </option>

            <option value="in_progress">
              In Progress
            </option>

            <option value="waiting">
              Waiting
            </option>

            <option value="resolved">
              Resolved
            </option>

            <option value="closed">
              Closed
            </option>

          </select>

          <select
            className="support-select"
            value={priority}
            onChange={(e) =>
              setPriority(
                e.target.value
              )
            }
          >

            <option value="">
              Priority
            </option>

            <option value="low">
              Low
            </option>

            <option value="normal">
              Normal
            </option>

            <option value="high">
              High
            </option>

          </select>

          <input
            className="support-input"
            placeholder="Queue ID"
            value={queueId}
            onChange={(e) =>
              setQueueId(
                e.target.value
              )
            }
          />

          <input
            className="support-input"
            placeholder="Assigned Admin ID"
            value={assigned}
            onChange={(e) =>
              setAssigned(
                e.target.value
              )
            }
          />

        </div>

        {selectedIds.length > 0 && (

          <div className="support-actions">

            <button className="support-table-btn"
              onClick={() =>
                bulkUpdate(
                  "resolved"
                )
              }
            >
              Resolve
            </button>

            <button className="support-icon-btn"
              onClick={() =>
                bulkUpdate(
                  "closed"
                )
              }
            >
              Close
            </button>

          </div>
        )}

      </div>

      {/* ===================================================
          TABLE
      =================================================== */}

      <div
        className="support-table-wrap"
        ref={containerRef}
        onScroll={handleScroll}
      >

        <table className="support-table">

          <thead>

            <tr>

              <th></th>

              <th>ID</th>

              <th>Queue</th>

              <th>Subject</th>

              <th>Source</th>

              <th>Priority</th>

              <th>Status</th>

              <th>SLA</th>

              <th>Escalation</th>

              <th>Assigned</th>

              <th></th>

            </tr>

          </thead>

          <tbody>

            {tickets.map(t => (

              <tr key={t.ticket_id}>

                <td>

                  <input
                    type="checkbox"
                    checked={selectedIds.includes(
                      t.ticket_id
                    )}
                    onChange={(e) => {

                      if (
                        e.target.checked
                      ) {

                        setSelectedIds(
                          prev => [
                            ...prev,
                            t.ticket_id
                          ]
                        );

                      } else {

                        setSelectedIds(
                          prev =>
                            prev.filter(
                              id =>
                                id !==
                                t.ticket_id
                            )
                        );
                      }
                    }}
                  />

                </td>

                <td>
                  #{t.ticket_id}
                </td>

                <td>
                  {t.queue_name || "—"}
                </td>

                <td>
                  {t.subject}
                </td>

                <td>
                  {t.source_name}
                </td>

                <td>

                  <span
                    className={`support-priority ${t.priority}`}
                  >
                    {t.priority}
                  </span>

                </td>

                <td>

                  <span
                    className={`support-status ${t.status}`}
                  >
                    {t.status}
                  </span>

                </td>

                <td>

                  {t.sla_breached ? (

                    <span className="support-badge is-breached">
                      BREACHED
                    </span>

                  ) : (

                    <span className="support-badge is-sla-safe">
                      OK
                    </span>

                  )}

                </td>

                <td>

                  {t.escalation_level > 0 && (

                    // <span
                    //   className={`zt_escalation l${t.escalation_level}`}
                    // >
                    //   L{t.escalation_level}
                    // </span>

                    <span className="support-badge is-critical">
                      L{t.escalation_level}
                    </span>

                  )}

                </td>

                <td>

                  {t.assigned_admin_name || "—"}

                </td>

                <td>

                  <button
                    className="support-table-btn"
                    onClick={() =>
                      openTicket(
                        t.ticket_id
                      )
                    }
                  >
                    <FaEye />
                    View
                  </button>

                </td>

              </tr>
            ))}

          </tbody>

        </table>

      </div>

      {/* ===================================================
          MODAL
      =================================================== */}

      {modalOpen &&
        selectedTicket &&

        ReactDOM.createPortal(

          <div
            className="support-modal-overlay"
            onClick={() =>
              setModalOpen(false)
            }
          >

            <div
              className="support-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              <div className="support-modal-header">

                <h3>
                  Ticket #
                  {
                    selectedTicket.ticket.ticket_id
                  }
                </h3>

                <button
                  onClick={() =>
                    setModalOpen(false)
                  }
                >
                  ✕
                </button>

              </div>

              <div className="support-modal-body">

                <div className="support-ticket-info">

                  <p>
                    <b>Queue:</b>
                    {" "}
                    {selectedTicket.ticket.queue_name || "—"}
                  </p>

                  <p>
                    <b>Subject:</b>
                    {" "}
                    {selectedTicket.ticket.subject}
                  </p>

                  <p>
                    <b>Status:</b>
                    {" "}
                    {selectedTicket.ticket.status}
                  </p>

                  <p>
                    <b>Priority:</b>
                    {" "}
                    {selectedTicket.ticket.priority}
                  </p>

                  <p>
                    <b>Waiting:</b>
                    {" "}
                    {selectedTicket.ticket.waiting_on_user
                      ? "YES"
                      : "NO"}
                  </p>

                  <p>
                    <b>Source:</b>
                    {" "}
                    {selectedTicket.ticket.source_name}
                  </p>

                  <p>
                    <b>Assigned:</b>
                    {" "}
                    {selectedTicket.ticket.assigned_admin_id || "—"}
                  </p>

                  <p>
                    <b>Escalation:</b>
                    {" "}
                    L{
                      selectedTicket.ticket.escalation_level || 0
                    }
                  </p>

                </div>

                {/* =======================================
                    ASSIGN
                ======================================= */}

                {selectedTicket.ticket.status !== "closed" && (

                  <div className="support-actions">

                    <select
                      className="support-select"
                      defaultValue=""
                      onChange={(e) => {

                        if (e.target.value) {

                          assignTicket(
                            selectedTicket.ticket.ticket_id,
                            e.target.value
                          );
                        }
                      }}
                    >

                      <option value="">
                        Assign Admin
                      </option>

                      {admins.map(
                        admin => (

                          <option
                            key={admin.admin_id}
                            value={admin.admin_id}
                          >
                            {admin.name}
                          </option>

                        )
                      )}

                    </select>

                  </div>
                )}

                {/* =======================================
                    STATUS ACTIONS
                ======================================= */}

                <div className="support-actions">

                  {getAllowedActions(
                    selectedTicket.ticket
                  ).includes(
                    "in_progress"
                  ) && (

                      <button className="support-table-btn"
                        onClick={() =>
                          changeStatus(
                            selectedTicket.ticket.ticket_id,
                            "in_progress"
                          )
                        }
                      >
                        Start
                      </button>

                    )}

                  {getAllowedActions(
                    selectedTicket.ticket
                  ).includes(
                    "waiting"
                  ) && (

                      <button className="support-table-btn"
                        onClick={() =>
                          changeStatus(
                            selectedTicket.ticket.ticket_id,
                            "waiting"
                          )
                        }
                      >
                        Waiting
                      </button>

                    )}

                  {getAllowedActions(
                    selectedTicket.ticket
                  ).includes(
                    "resolved"
                  ) && (

                      <button className="support-table-btn"
                        onClick={() =>
                          changeStatus(
                            selectedTicket.ticket.ticket_id,
                            "resolved"
                          )
                        }
                      >
                        Resolve
                      </button>

                    )}

                  {getAllowedActions(
                    selectedTicket.ticket
                  ).includes(
                    "closed"
                  ) && (

                      <button className="support-table-btn"
                        onClick={() =>
                          changeStatus(
                            selectedTicket.ticket.ticket_id,
                            "closed"
                          )
                        }
                      >
                        Close
                      </button>

                    )}

                  {selectedTicket.ticket.status === "closed" && (

                    <button className="support-table-btn"
                      onClick={() =>
                        reopenTicket(
                          selectedTicket.ticket.ticket_id
                        )
                      }
                    >
                      Reopen
                    </button>

                  )}

                  {selectedTicket.ticket.status !== "closed" &&
                    selectedTicket.ticket.status !== "resolved" && (

                      <button className="support-table-btn"
                        onClick={() =>
                          changePriority(
                            selectedTicket.ticket.ticket_id,
                            "high"
                          )
                        }
                      >
                        High Priority
                      </button>

                    )}

                  {selectedTicket.ticket.status !== "closed" &&
                    selectedTicket.ticket.status !== "resolved" &&
                    selectedTicket.ticket.escalation_level < 2 && (

                      <button className="support-table-btn"
                        onClick={() =>
                          escalate(
                            selectedTicket.ticket.ticket_id
                          )
                        }
                      >
                        Escalate
                      </button>

                    )}

                  {selectedTicket.ticket.escalation_level > 0 &&
                    selectedTicket.ticket.status !== "closed" && (

                      <button className="support-table-btn"
                        onClick={() =>
                          deEscalate(
                            selectedTicket.ticket.ticket_id
                          )
                        }
                      >
                        De-Escalate
                      </button>

                    )}

                  {selectedTicket.ticket.status !== "closed" && (

                    <button className="support-table-btn"
                      onClick={() =>
                        unassignTicket(
                          selectedTicket.ticket.ticket_id
                        )
                      }
                    >
                      Unassign
                    </button>

                  )}

                </div>

                {/* =======================================
                    REPLY
                ======================================= */}

                {selectedTicket.ticket.status !== "closed" &&
                  selectedTicket.ticket.status !== "resolved" && (

                    <div className="support-note">

                      <h4>Reply</h4>

                      <textarea
                        className="support-input"
                        value={replyMessage}
                        onChange={(e) =>
                          setReplyMessage(
                            e.target.value
                          )
                        }
                        placeholder="Type reply..."
                      />

                      <button className="support-table-btn"
                        onClick={() =>
                          sendReply(
                            selectedTicket.ticket.ticket_id
                          )
                        }
                      >
                        Send Reply
                      </button>

                    </div>
                  )}

                {/* =======================================
                    NOTES
                ======================================= */}

                <div className="support-note">

                  <h4>
                    Internal Note
                  </h4>

                  <textarea
                    className="support-input"
                    value={internalNote}
                    onChange={(e) =>
                      setInternalNote(
                        e.target.value
                      )
                    }
                    placeholder="Internal admin note..."
                  />

                  <button className="support-table-btn"
                    onClick={() =>
                      addInternalNote(
                        selectedTicket.ticket.ticket_id
                      )
                    }
                  >
                    Add Note
                  </button>

                </div>

                {/* =======================================
                    HISTORY
                ======================================= */}

                <div className="support-timeline">

                  <h4>
                    Status History
                  </h4>

                  {(selectedTicket.history || []).map(
                    (
                      h,
                      index
                    ) => (

                      <div
                        key={index}
                        className="support-timeline-content"
                      >

                        <b>
                          {h.old_status}
                        </b>

                        {" → "}

                        <b>
                          {h.new_status}
                        </b>

                      </div>
                    )
                  )}

                </div>

                {/* =======================================
                    ATTACHMENTS
                ======================================= */}

                <div className="support-attachments">

                  <h4>
                    Attachments
                  </h4>

                  {selectedTicket.attachments.map(
                    a => (

                      <div
                        key={a.attachment_id}
                      >

                        {attachmentUrls[
                          a.attachment_id
                        ] ? (

                          <img
                            src={
                              attachmentUrls[
                              a.attachment_id
                              ]
                            }
                            alt=""
                            onClick={() =>
                              setPreviewFile(
                                attachmentUrls[
                                a.attachment_id
                                ]
                              )
                            }
                          />

                        ) : (

                          <button
                            onClick={async () => {

                              const url =
                                await fetchAttachment(
                                  a.attachment_id
                                );

                              if (url) {
                                window.open(url);
                              }
                            }}
                          >
                            {a.file_name}
                          </button>

                        )}

                      </div>
                    )
                  )}

                </div>

              </div>

            </div>

          </div>,

          document.body
        )}

      {/* ===================================================
          PREVIEW
      =================================================== */}

      {previewFile &&

        ReactDOM.createPortal(

          <div
            className="support-modal-overlay"
            onClick={() =>
              setPreviewFile(null)
            }
          >

            <img
              src={previewFile}
              className="support-preview-image"
            />

          </div>,

          document.body
        )}

    </div>
  );
}